import i18n, { TFunction } from 'i18next';
import moment from 'moment';
import * as yup from 'yup';
import { object, string } from 'yup';

import addressValidationSchema from '@components/Formik/Address/validation';
import personalNameSchema from '@components/Formik/PersonalNameForm/validation';
import {
  firstNameSchema,
  lastNameSchema,
  latinOnlyEmailSchema,
  numberRequiredSchema,
  numberSchema,
} from '@components/Formik/validation/fieldDefinitions';
import {
  isRequired,
  schemaField,
} from '@components/Formik/validation/schemaDefinitions';

import { Gender } from '@typings/graphql';

const { t } = i18n;
const genderMax = 255;

const phoneRequiredSchema = (t: TFunction) => {
  return yup.object().shape(
    {
      phone_code: yup
        .string()
        .when(
          'phone_number',
          (number: string, schema: yup.StringSchema<string>) => {
            if (number) {
              return isRequired(t, schema);
            }
            return schema;
          }
        ),
      phone_number: yup.string().when('phone_code', (country: string) => {
        if (country) {
          return numberRequiredSchema(t);
        }
        return numberSchema(t);
      }),
    },
    [
      ['phone_code', 'phone_number'],
      ['phone_number', 'phone_code'],
    ]
  );
};

const genderSchema = object({
  gender: string().nullable(),
  genderDescription: string()
    .nullable()
    .when('gender', {
      is: Gender.Self,
      then: string()
        .trim()
        .required(t('form.field.error_required', 'Required'))
        .max(
          255,
          t(
            'form.field.error_too_long',
            'Field cannot exceed {{number}} characters',
            { number: genderMax }
          )
        ),
    }),
});

const genderSchemaForConfirmCandidate = object({
  gender: string()
    .nullable()
    .required(t('form.field.error_required', 'Required')),
  genderDescription: string()
    .nullable()
    .when('gender', {
      is: Gender.Self,
      then: string()
        .trim()
        .required(t('form.field.error_required', 'Required'))
        .max(
          255,
          t(
            'form.field.error_too_long',
            'Field cannot exceed {{number}} characters',
            { number: genderMax }
          )
        ),
    }),
});

const AddCandidateContactDetailSchema = (t: TFunction) => {
  const schema = {
    firstName: firstNameSchema(t),
    lastName: lastNameSchema(t),
  };

  return yup.object(schema);
};

export const personalDetailsValidationSchema = (t: TFunction) =>
  yup
    .object()
    .shape({
      email: latinOnlyEmailSchema(t).required(
        t('form.field.error_required', 'Required')
      ),
    })
    .concat(AddCandidateContactDetailSchema(t))
    .concat(phoneRequiredSchema(t))
    .concat(genderSchema);

const dateOfBirthSchemaForConfirmCandidate = (t: TFunction) => {
  const currentDate = new Date();

  return yup.object().shape(
    {
      dayOfBirth: yup
        .number()
        .min(1, t('form.field.invalid_day', 'Invalid day specified'))
        .when(
          ['monthOfBirth', 'yearOfBirth'],
          (
            monthOfBirth: string,
            yearOfBirth: string,
            schema: yup.StringSchema
          ) => {
            const maxDays = (daysInMonth: number) =>
              schema.max(
                daysInMonth,
                t('form.field.invalid_day', 'Invalid day specified')
              );

            if (monthOfBirth && yearOfBirth) {
              const daysInMonth = moment(
                `${yearOfBirth}-${monthOfBirth}`
              ).daysInMonth();

              return maxDays(daysInMonth).required(
                t('form.field.error_required', 'Required')
              );
            }

            if (monthOfBirth) {
              const daysInSelectedMonth = moment()
                .month(Number(monthOfBirth) - 1)
                .daysInMonth();

              return maxDays(daysInSelectedMonth).required(
                t('form.field.error_required', 'Required')
              );
            }

            return maxDays(31);
          }
        )
        .integer(),
      monthOfBirth: yup
        .string()
        .when(
          'dayOfBirth',
          (dayOfBirth: string | number, schema: yup.StringSchema) => {
            if (dayOfBirth) {
              return isRequired(t, schema);
            }

            return schema;
          }
        ),
      yearOfBirth: yup
        .number()
        .required(t('form.field.error_required', 'Required'))
        .max(
          currentDate.getFullYear(),
          t('form.field.invalid_year', 'Invalid year specified')
        )
        .min(
          currentDate.getFullYear() - 100,
          t('form.field.invalid_year', 'Invalid year specified')
        )
        .when(
          ['dayOfBirth', 'monthOfBirth'],
          (
            dayOfBirth: number,
            monthOfBirth: string,
            schema: yup.StringSchema
          ) => {
            if (dayOfBirth || monthOfBirth) {
              return isRequired(t, schema);
            }

            return schema;
          }
        )
        .integer(),
    },
    [
      ['dayOfBirth', 'monthOfBirth'],
      ['monthOfBirth', 'dayOfBirth'],
      ['dayOfBirth', 'yearOfBirth'],
      ['yearOfBirth', 'dayOfBirth'],
    ]
  );
};

export const ConfirmCandidateValidationSchema = (t: TFunction) =>
  yup
    .object({
      email: latinOnlyEmailSchema(t).required(
        t('form.field.error_required', 'Required')
      ),
      primaryAddress: addressValidationSchema(t),
      primaryPhone: yup.object().shape(
        {
          country: yup
            .string()
            .when(
              'number',
              (number: string, schema: yup.StringSchema<string>) => {
                if (number) {
                  return isRequired(t, schema);
                }

                return schema;
              }
            ),
          number: yup.string().when('country', (country: string) => {
            if (country) {
              return numberRequiredSchema(t);
            }

            return numberSchema(t);
          }),
        },
        [
          ['country', 'number'],
          ['number', 'country'],
        ]
      ),
      primaryLanguage: yup
        .string()
        .required(t('form.field.error_required', 'Required')),
    })
    .concat(personalNameSchema(t, true))
    .concat(dateOfBirthSchemaForConfirmCandidate(t))
    .concat(genderSchemaForConfirmCandidate);

const stringReqSchema = (t: TFunction) =>
  schemaField(t, yup.string(), isRequired);

export const membershipInformationValidationSchema = (t: TFunction) =>
  yup.object().shape({
    procedureCertification: schemaField(t, yup.boolean()),
    memberType: stringReqSchema(t),
    admissionDate: stringReqSchema(t).nullable(),
    sponsoredBy: yup.array().of(stringReqSchema(t)),
  });
